import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Eye, EyeOff, Sparkles, CheckCircle } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { SeoHead } from "@/components/seo/SeoHead";
import { supabase } from "@/integrations/supabase/client";
import { humanizeSupabaseError } from "@/lib/supabase-errors";

const ResetPassword = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);
  
  const { updatePassword } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  // Valida o link de recuperação de forma resiliente:
  // o cliente de auth pode consumir o token da URL antes desta página ler o hash,
  // então também aceitamos uma sessão já estabelecida (evita falso "Link inválido").
  useEffect(() => {
    let cancelled = false;

    const hashParams = new URLSearchParams(window.location.hash.substring(1));
    const accessToken = hashParams.get('access_token');
    const type = hashParams.get('type');
    const errorCode = hashParams.get('error_code');
    const errorDescription = hashParams.get('error_description');

    // Link expirado ou já usado — o servidor devolve o erro no hash
    if (errorCode) {
      toast({
        title: "Link expirado",
        description:
          errorCode === 'otp_expired'
            ? "Este link de recuperação expirou ou já foi usado. Solicite um novo."
            : errorDescription?.replace(/\+/g, ' ') || "Este link de recuperação é inválido.",
        variant: "destructive",
      });
      setTimeout(() => navigate("/auth"), 2500);
      return;
    }

    // Token ainda presente no hash — link válido
    if (accessToken && type === 'recovery') {
      return;
    }

    // O hash pode já ter sido processado/removido pelo cliente de auth.
    // Verifica (com tentativas) se existe uma sessão antes de declarar inválido.
    const validateSession = async () => {
      for (let i = 0; i < 5; i++) {
        if (cancelled) return;
        const { data: { session } } = await supabase.auth.getSession();
        if (session) return; // Sessão de recuperação ativa — link válido
        await new Promise((r) => setTimeout(r, 600));
      }
      if (cancelled) return;
      toast({
        title: "Link inválido",
        description: "Este link de recuperação é inválido ou já foi usado.",
        variant: "destructive",
      });
      setTimeout(() => {
        if (!cancelled) navigate("/auth");
      }, 2000);
    };
    validateSession();

    return () => {
      cancelled = true;
    };
  }, [navigate, toast]);

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();

    if (newPassword.length < 6) {
      toast({
        title: "Senha muito curta",
        description: "A senha deve ter no mínimo 6 caracteres.",
        variant: "destructive",
      });
      return;
    }

    if (newPassword !== confirmPassword) {
      toast({
        title: "Senhas não coincidem",
        description: "As senhas digitadas não são iguais.",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    const { error } = await updatePassword(newPassword);

    if (error) {
      toast({
        title: "Erro ao redefinir senha",
        description: humanizeSupabaseError(error, {
          fallback: "Não foi possível redefinir a senha. Tente novamente.",
        }),
        variant: "destructive",
      });
      setIsLoading(false);
    } else {
      setResetSuccess(true);
      toast({
        title: "Senha redefinida!",
        description: "Sua senha foi alterada com sucesso.",
      });
      setTimeout(() => navigate("/auth"), 3000);
    }
  };

  if (resetSuccess) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-primary/5 via-background to-secondary/20 p-4">
        <SeoHead title="Redefinir senha | FabChat" noindex />
        <Link to="/" className="flex items-center justify-center font-bold text-2xl mb-8">
          <img src="/logo-light.png?v=6" alt="FabChat Logo" className="h-10 sm:h-12 w-auto object-contain block dark:hidden" />
          <img src="/logo.png?v=6" alt="FabChat Logo" className="h-10 sm:h-12 w-auto object-contain hidden dark:block" />
        </Link>

        <Card className="w-full max-w-md shadow-lg">
          <CardContent className="pt-6 pb-6 flex flex-col items-center text-center space-y-4">
            <div className="w-16 h-16 rounded-full bg-green-500/10 flex items-center justify-center">
              <CheckCircle className="w-8 h-8 text-green-500" />
            </div>
            <div>
              <h2 className="text-2xl font-bold mb-2">Senha alterada!</h2>
              <p className="text-muted-foreground">
                Sua senha foi redefinida com sucesso. Você será redirecionado para o login.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-primary/5 via-background to-secondary/20 p-4">
      <SeoHead title="Redefinir senha | FabChat" noindex />
      <Link to="/" className="flex items-center justify-center font-bold text-2xl mb-8">
        <img src="/logo.png?v=6" alt="FabChat Logo" className="h-10 sm:h-12 w-auto object-contain" />
      </Link>

      <Card className="w-full max-w-md shadow-lg">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl">Redefinir Senha</CardTitle>
          <CardDescription>
            Digite sua nova senha abaixo
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          <form onSubmit={handleResetPassword} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="new-password">Nova Senha</Label>
              <div className="relative">
                <Input
                  id="new-password"
                  type={showNewPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  minLength={6}
                  className="pr-10"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                >
                  {showNewPassword ? (
                    <EyeOff className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <Eye className="h-4 w-4 text-muted-foreground" />
                  )}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">Mínimo 6 caracteres</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirm-password">Confirmar Nova Senha</Label>
              <div className="relative">
                <Input
                  id="confirm-password"
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  minLength={6}
                  className="pr-10"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? (
                    <EyeOff className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <Eye className="h-4 w-4 text-muted-foreground" />
                  )}
                </Button>
              </div>
            </div>

            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? "Redefinindo..." : "Redefinir Senha"}
            </Button>

            <div className="text-center">
              <Link to="/auth" className="text-sm text-primary hover:underline">
                Voltar para login
              </Link>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default ResetPassword;
